/**
 * How the analytics screen turns "which period?" into the two calendar dates the
 * contract asks for — and nothing else.
 *
 * **Nothing here decides what a range means.** The boundaries, the half-open
 * rule, the inclusive-to-exclusive conversion, the 366-day cap and the refusal
 * to fall back to UTC all live in `@wend/shared`'s `analytics.ts` and
 * `report.ts`: `analyticsRangeRequestSchema`, `resolveAnalyticsRange`,
 * `describeReportWindow`, `windowContains`. This file adds the one thing a
 * contract cannot hold — what a period *picker* offers — and it is deliberately
 * the smallest surface that can do that.
 *
 * **The presets are resolved with the scheduler's own function, then read back
 * as dates.** "The month that just ended" is `resolveReportWindow('previous_month',
 * …)`, exactly the call the nightly report schedule makes, and the two calendar
 * dates it produces come from `zonedIsoDate` over the window's own instants.
 * The rejected alternative was calendar arithmetic here — `Date.UTC(year, month
 * - 1, 0)` for "the last day of last month" — which is easy, looks obviously
 * right, and is a *second opinion* about where a month starts in a named zone.
 * Two opinions agree until the day one of them is edited, and the symptom is a
 * range report and a scheduled workbook covering different days while both
 * print a label saying "September". So: one opinion, borrowed.
 *
 * **Borrowing an opinion means inheriting its defects, and this file inherited
 * one.** `resolveReportWindow` walked backwards from `to − 1ms`, and
 * `zonedStartOf` used to answer an ambiguous local midnight with the *later* of
 * its two occurrences — so in a zone that falls back through midnight on the 1st
 * (America/Havana, 2026-11-01) the walk never left the day it started in and
 * "The month that just ended" filled these fields with a **single day of the
 * current month**, under that label, with nothing on the screen to say so. The
 * fix belongs where the opinion lives and is there: `zonedStartOf` now takes the
 * earliest instant that reads as the boundary. This file deliberately does not
 * add a width check of its own — a second opinion about how long a month is, to
 * guard against the first one being wrong, is how the two drift apart in the
 * first place.
 *
 * **The inclusive last day is `to − 1ms`, formatted in the zone.** A window is
 * half-open, a date picker is inclusive, and the day *named* by an exclusive
 * upper bound is the day after the last day the window contains. `reportFileName`
 * in the contract makes the same conversion the same way and documents why:
 * printing `…-to-2026-10-01` on a period holding nothing from October is the
 * small lie that gets a correct report deleted as wrong.
 *
 * **The presets end yesterday, not today, and that is a decision.** A period
 * ending today has not finished: the same dates re-read this evening give
 * different numbers, and two people comparing "the last 30 days" an hour apart
 * are comparing different periods. Slice 13 settled this for scheduled windows —
 * `previous_day` and `previous_month` are closed periods and there is
 * deliberately no `last_30_days` rule that ends "now" — and the argument does
 * not weaken because a person is doing the asking. Today is still reachable:
 * anybody can type it into the last-day field, and {@link rangeEndsInFuture} is
 * what makes the screen say out loud that the period is still open.
 *
 * **`shiftCalendarDate` is calendar arithmetic on a zoneless date, never instant
 * arithmetic.** `2026-03-08` minus six days is `2026-03-02` in every zone on
 * earth, including the ones that lost an hour in between; subtracting
 * `6 × 86,400,000` from an instant is the version that lands on the wrong day
 * twice a year. `nextCalendarDate` in the contract is the same move in the one
 * direction it needs; this steps either way, which is why it is here and not a
 * loop around that function.
 *
 * Everything returns `null` rather than a fallback when the platform cannot
 * resolve the zone. There is no safe default: a period bucketed against a clock
 * nobody chose is confidently wrong and unreadable as wrong, which is the whole
 * argument of this slice.
 */
import {
  REPORT_WINDOW_RULE_LABELS,
  parseCalendarDate,
  resolveReportWindow,
  zonedIsoDate,
  type AnalyticsRange,
  type CalendarDate,
} from '@wend/shared';

/* -------------------------------------------------------------------------- */
/* Calendar dates                                                              */
/* -------------------------------------------------------------------------- */

/** A first and a last day, both **included**, as `analyticsRangeRequestSchema` reads them. */
export interface AnalyticsDates {
  readonly fromDate: CalendarDate;
  readonly toDate: CalendarDate;
}

/** `2026-09-05` from the parts of a day. Zero-padded, so it round-trips the regex. */
function calendarDateOf(year: number, monthIndex: number, day: number): CalendarDate {
  // `Date.UTC` normalises an overflowing day or month — 0 is the last day of the
  // previous month, 32 is the 1st of the next — so callers need not know how long
  // a month is, and no code here has an opinion about February.
  const normalised = new Date(Date.UTC(year, monthIndex, day));
  const yyyy = String(normalised.getUTCFullYear()).padStart(4, '0');
  const mm = String(normalised.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(normalised.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * A calendar date `days` earlier or later. `null` when the input is not a day.
 *
 * Calendar arithmetic, not duration arithmetic: the input carries no zone and
 * therefore no length, and the answer wanted is "six days earlier on a calendar"
 * rather than "144 hours ago, whatever day that turns out to be".
 */
export function shiftCalendarDate(value: string, days: number): CalendarDate | null {
  const parts = parseCalendarDate(value);
  if (parts === null) return null;
  return calendarDateOf(parts.year, parts.month - 1, parts.day + days);
}

/* -------------------------------------------------------------------------- */
/* What the picker offers                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The periods the control offers.
 *
 * Three closed ones and an explicit pair of dates. `previous_month` shares its
 * name with the contract's own window rule on purpose — it resolves through
 * `resolveReportWindow` and takes `REPORT_WINDOW_RULE_LABELS`'s wording — while
 * the two day-count presets are this screen's own and are *not* rules: nothing
 * schedules "the last 30 days", because a rule that ends now is not reproducible.
 */
export const ANALYTICS_PERIOD_CHOICE_VALUES = [
  'last_7_days',
  'last_30_days',
  'previous_month',
  'custom',
] as const;
export type AnalyticsPeriodChoice = (typeof ANALYTICS_PERIOD_CHOICE_VALUES)[number];

/** How many days each day-count preset covers. The label below reads this. */
const PRESET_DAYS: Readonly<Record<'last_7_days' | 'last_30_days', number>> = {
  last_7_days: 7,
  last_30_days: 30,
};

export const ANALYTICS_PERIOD_LABEL: Readonly<Record<AnalyticsPeriodChoice, string>> = {
  last_7_days: `The ${String(PRESET_DAYS.last_7_days)} days up to yesterday`,
  last_30_days: `The ${String(PRESET_DAYS.last_30_days)} days up to yesterday`,
  previous_month: REPORT_WINDOW_RULE_LABELS.previous_month,
  custom: 'Specific dates',
};

export const ANALYTICS_PERIOD_DESCRIPTION: Readonly<Record<AnalyticsPeriodChoice, string>> = {
  last_7_days: 'Whole days that have finished, so these figures will not move under you.',
  last_30_days: 'Whole days that have finished. Today is left out until it is over.',
  previous_month:
    'The month a monthly report covers, resolved by the same function the schedule uses.',
  custom: 'Both dates are included. Pick today as the last day to see a period still running.',
};

/* -------------------------------------------------------------------------- */
/* Resolving a choice into two dates                                           */
/* -------------------------------------------------------------------------- */

/**
 * The last day that has finished, in the tenant's zone. `null` when the zone
 * cannot be resolved.
 *
 * Borrowed from `resolveReportWindow('previous_day', …)` rather than computed as
 * "today minus one": today in a zone is itself a zoned question, and the two
 * places that answer it would then be this file and the contract.
 */
export function lastClosedDay(now: Date, timezone: string): CalendarDate | null {
  const yesterday = resolveReportWindow('previous_day', now, timezone);
  if (yesterday === null) return null;
  return zonedIsoDate(new Date(yesterday.from), timezone);
}

/**
 * The month that just ended, as the two dates a picker holds. `null` when the
 * zone cannot be resolved.
 *
 * `toDate` is the window's exclusive upper bound moved back one millisecond and
 * then formatted in the zone — the last day the window actually contains, which
 * is the day a person means when they pick it.
 */
export function previousMonthDates(now: Date, timezone: string): AnalyticsDates | null {
  const month = resolveReportWindow('previous_month', now, timezone);
  if (month === null) return null;
  return {
    fromDate: zonedIsoDate(new Date(month.from), timezone),
    toDate: zonedIsoDate(new Date(Date.parse(month.to) - 1), timezone),
  };
}

/**
 * The two dates a choice names, or `null` when they cannot be established.
 *
 * `custom` hands back exactly what is in the fields, unchecked: the contract is
 * what refuses a reversed range, an impossible day like `2026-02-30` or a period
 * longer than the cap, and a second copy of those rules here would be a second
 * opinion about what this app may ask for. It only fails closed on the presets,
 * where the failure is the platform's rather than the reader's.
 */
export function analyticsDatesForChoice(
  choice: AnalyticsPeriodChoice,
  now: Date,
  timezone: string,
  custom: AnalyticsDates,
): AnalyticsDates | null {
  if (choice === 'custom') return custom;
  if (choice === 'previous_month') return previousMonthDates(now, timezone);

  const lastDay = lastClosedDay(now, timezone);
  if (lastDay === null) return null;

  // Inclusive on both ends, so the span is `days - 1` steps back, not `days`.
  const firstDay = shiftCalendarDate(lastDay, -(PRESET_DAYS[choice] - 1));
  if (firstDay === null) return null;
  return { fromDate: firstDay, toDate: lastDay };
}

/* -------------------------------------------------------------------------- */
/* Whether the period is over                                                  */
/* -------------------------------------------------------------------------- */

/**
 * True when the range runs past this moment — a period still collecting sends.
 *
 * The comparison is against the range's **exclusive upper bound**, which is the
 * first instant it does not contain. A range whose last included day is today
 * ends at midnight tonight, so this is true all day and stops being true the
 * moment the day closes, which is exactly when the numbers stop moving.
 */
export function rangeEndsInFuture(range: AnalyticsRange, now: Date): boolean {
  return Date.parse(range.to) > now.getTime();
}
